/* istanbul ignore file */

import { mslaGetToken, mslaLoginPopup, mslaLogout } from '@shape/util-ui';
import { ADD_AUTH_TOKEN, AppContext, REMOVE_AUTH_TOKEN } from 'appContext';
import * as constants from 'config/constants';
import * as paths from 'config/paths';
import { usePostHog } from 'posthog-js/react';
import { usePermissionsQuery, useWorkspaceQuery } from 'queries/userAndPermissions';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { MSAL_CONFIG } from 'utils/auth';
import axiosAPI from 'utils/axios';
import { useGlobalNotification } from './useGlobalNotification';
import { useFakeAuthentication } from './useFakeAuthentication';
import { useAuth as useAuthContext } from '@shape/auth-context';

export const useAuth = () => {
  const navigate = useNavigate();
  const { displaySnackbar } = useGlobalNotification();
  const { t } = useTranslation();
  const { state, dispatch } = useContext(AppContext);
  const { setAuth } = useAuthContext();

  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = Boolean(state.auth?.token);

  const location = useLocation();
  const posthog = usePostHog();

  const permissionRequest = usePermissionsQuery({ enabled: isAuthenticated });

  const workspaceQuery = useWorkspaceQuery({ enabled: false });
  const email = localStorage.getItem(constants.B2C_LOCAL_STORAGE_EMAIL) || '';
  const domainHint = localStorage.getItem(constants.B2C_LOCAL_STORAGE_DOMAIN_HINT) || '';
  const msalConfig = MSAL_CONFIG(email, domainHint);

  const login = (email: string, domainHint: string) => {
    mslaLoginPopup(
      MSAL_CONFIG(email, domainHint),
      (token) => {
        const event = new CustomEvent(ADD_AUTH_TOKEN, { detail: token });
        window.dispatchEvent(event);
        navigate(location.state?.from || paths.HOME_PAGE);
      },
      () => {
        navigate(paths.HOME_PAGE);
      },
    );
  };

  const logout = async () => {
    await mslaLogout(MSAL_CONFIG(email, domainHint));
    localStorage.removeItem(constants.B2C_LOCAL_STORAGE_EMAIL);
    localStorage.removeItem(constants.LOCAL_STORAGE_WORKSPACE_ID);
    dispatch({ type: REMOVE_AUTH_TOKEN });
    navigate(paths.LOGIN_PAGE);
    posthog?.reset();
  };

  useEffect(() => {
    const fetchUser = async () => {
      await mslaGetToken(
        msalConfig,
        (token) => {
          axiosAPI.defaults.headers.common.Authorization = `Bearer ${token}`;

          workspaceQuery.refetch();
          dispatch({ type: ADD_AUTH_TOKEN, token });
          setAuth({ ...state?.auth, token });
          setIsLoading(false);
        },
        () => {
          dispatch({ type: REMOVE_AUTH_TOKEN });
          setIsLoading(false);
        },
      );
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (permissionRequest.data) {
      dispatch({ type: 'SET_USER_ROLES', data: permissionRequest.data.permissions });
    }
  }, [permissionRequest.data]);

  useEffect(() => {
    if (workspaceQuery.isSuccess) {
      localStorage.setItem(constants.LOCAL_STORAGE_WORKSPACE_ID, workspaceQuery.data[0]?.id);
    } else if (workspaceQuery.isError) {
      localStorage.removeItem(constants.LOCAL_STORAGE_WORKSPACE_ID);
      displaySnackbar({
        message: t('messages:workspaceError.message'),
        type: 'error',
      });
    }
  }, [workspaceQuery]);

  const { name, given_name, family_name } = state.auth?.user ?? {};
  const username = (given_name && family_name && `${given_name} ${family_name}`) || name || '';

  return {
    username,
    isAuthenticated,
    isLoading,
    login,
    logout,
  };
};

export const useAuthentication = constants.IS_DEMO ? useFakeAuthentication : useAuth;
